import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "../styles/SelectRaces.css";

import defaultCalendarConfig from "../../config/defaultCalendarConfig.json";

import vallon from "../../medias/png/icons/vallon.png";
import chrono from "../../medias/png/icons/chrono.png";
import plat from "../../medias/png/icons/plat.png";
import montagne from "../../medias/png/icons/montagne.png";

const MAX_PRO_SERIES = 42;
const MAX_CONTINENTAL = 55;

const SelectRaces = ({
  selectedRaces,
  setSelectedRaces,
  setBonusRaces,
  bonusRaces,
  races,
  nextStep,
  prevStep,
}) => {
  const { t } = useTranslation();

  const icons = {
    Vallon: vallon,
    Chrono: chrono,
    Plat: plat,
    Montagne: montagne,
  };

  const [categoryChoice, setCategoryChoice] = useState("UCI");
  const [calendarDefaultValue, setCalendarDefaultValue] = useState("aucun");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [errorMessage, setErrorMessage] = useState("");

  const getRaceId = (r) => r?.raceId ?? r?.id;

  const normalizeDivisionLabel = (race) => {
    if (race?.division) return String(race.division);

    const g = Number(race?.division);
    if (g === 0) return "World Tour";
    if (g === 1) return "Continental";
    if (g === 2) return "Pro Series";
    if (g === 3) return "International";
    return "—";
  };

  const isObligatory = (race) => {
    const d = normalizeDivisionLabel(race).toLowerCase().trim();
    if (d === "world tour" || d === "majeur") return true;

    const g = Number(race?.division);
    if (g === 0 || g === 3) return true;

    return false;
  };

  const isProSeries = (race) => {
    const d = normalizeDivisionLabel(race).toLowerCase().trim();
    if (d.includes("pro series")) return true;
    return Number(race?.division) === 2;
  };

  const isContinental = (race) => {
    const d = normalizeDivisionLabel(race).toLowerCase().trim();
    if (d.includes("continental")) return true;
    return Number(race?.division) === 1;
  };

  const parseDateForSort = (race) => {
    const raw = race?.date_start ?? race?.dateStart ?? race?.date;
    if (!raw) return new Date(2100, 0, 1);

    if (typeof raw === "string" && raw.includes("-") && raw.split("-").length === 3) {
      const [dd, mm, yyyy] = raw.split("-").map((x) => parseInt(x, 10));
      if (!Number.isNaN(dd) && !Number.isNaN(mm) && !Number.isNaN(yyyy)) {
        return new Date(yyyy, mm - 1, dd);
      }
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return new Date(2100, 0, 1);
    return d;
  };

  const flagPath = (country) => {
    if (!country) return "";
    return `${process.env.PUBLIC_URL}/flags/${country
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "_")
      .toLowerCase()}.png`;
  };

  const editableRaces = useMemo(() => {
    if (!Array.isArray(races)) return [];
    return races.filter((r) => !isObligatory(r) && (isProSeries(r) || isContinental(r)));
  }, [races]);

  const competitionNumber = categoryChoice === "UCI" ? 2 : 1;

  const currentCategoryRaces = useMemo(() => {
    return editableRaces.filter((r) => (competitionNumber === 2 ? isProSeries(r) : isContinental(r)));
  }, [editableRaces, competitionNumber]);

  const viewRaces = useMemo(() => {
    let list = [...currentCategoryRaces];

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((r) => {
        const name = (r?.name ?? "").toLowerCase();
        const type = (r?.courseType ?? "").toLowerCase();
        return name.includes(term) || type.includes(term);
      });
    }

    switch (sortBy) {
      case "name":
        list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
        break;
      case "date":
        list.sort((a, b) => parseDateForSort(a) - parseDateForSort(b));
        break;
      case "type":
        list.sort((a, b) => (a?.courseType ?? "").localeCompare(b?.courseType ?? ""));
        break;
      case "division":
        list.sort((a, b) => normalizeDivisionLabel(a).localeCompare(normalizeDivisionLabel(b)));
        break;
      default:
        break;
    }

    return list;
  }, [currentCategoryRaces, searchTerm, sortBy]);

  const selectedProCount = useMemo(() => {
    return (selectedRaces || []).filter((r) => isProSeries(r) && !isObligatory(r)).length;
  }, [selectedRaces]);

  const selectedContCount = useMemo(() => {
    return (selectedRaces || []).filter((r) => isContinental(r) && !isObligatory(r)).length;
  }, [selectedRaces]);

  const selectedInCurrent = categoryChoice === "UCI" ? selectedProCount : selectedContCount;
  const maxCurrent = categoryChoice === "UCI" ? MAX_PRO_SERIES : MAX_CONTINENTAL;

  const isRaceSelected = (raceId) => (selectedRaces || []).some((r) => getRaceId(r) === raceId);

  useEffect(() => {
    if (errorMessage) setErrorMessage("");
  }, [selectedRaces, categoryChoice]);

  const handleSelectRace = (race) => {
    if (!race || isObligatory(race)) return;

    const raceId = getRaceId(race);
    if (raceId == null) return;

    if (isRaceSelected(raceId)) {
      setSelectedRaces((selectedRaces || []).filter((r) => getRaceId(r) !== raceId));
      return;
    }

    if (categoryChoice === "UCI") {
      if (selectedProCount >= MAX_PRO_SERIES) {
        setErrorMessage(t("selectRaces.errors.maxProSeries", { max: MAX_PRO_SERIES }));
        return;
      }
    } else {
      if (selectedContCount >= MAX_CONTINENTAL) {
        setErrorMessage(t("selectRaces.errors.maxContinental", { max: MAX_CONTINENTAL }));
        return;
      }
    }

    setSelectedRaces([...(selectedRaces || []), race]);
  };

  const canGoNext =
    categoryChoice === "UCI"
      ? selectedProCount === MAX_PRO_SERIES
      : selectedProCount === MAX_PRO_SERIES && selectedContCount === MAX_CONTINENTAL;

  const handlePrevStep = () => {
    if (categoryChoice === "UCI") {
      prevStep();
    } else {
      setCategoryChoice("UCI");
    }
  };

  const handleNextStep = () => {
    if (categoryChoice === "UCI") {
      if (selectedProCount !== MAX_PRO_SERIES) {
        setErrorMessage(
          t("selectRaces.errors.needExactProSeries", {
            max: MAX_PRO_SERIES,
            current: selectedProCount,
          })
        );
        return;
      }
      setCategoryChoice("CONTINENTAL");
      return;
    }

    if (selectedProCount !== MAX_PRO_SERIES || selectedContCount !== MAX_CONTINENTAL) {
      setErrorMessage(
        t("selectRaces.errors.needExactBoth", {
          maxPro: MAX_PRO_SERIES,
          maxCont: MAX_CONTINENTAL,
          curPro: selectedProCount,
          curCont: selectedContCount,
        })
      );
      return;
    }

    nextStep();
  };

  const handleDefaultCalendar = (event) => {
    const selectedCalendar = event.target.value;
    setCalendarDefaultValue(selectedCalendar);

    setBonusRaces?.([]);

    if (selectedCalendar === "aucun") {
      setSelectedRaces([]);
      return;
    }

    const defaultRaceIds = defaultCalendarConfig?.[selectedCalendar] || [];
    const byId = new Set(defaultRaceIds);

    const defaultRaces = editableRaces.filter((r) => byId.has(getRaceId(r)));
    setSelectedRaces(defaultRaces);
  };

  const selectedForCategory = useMemo(() => {
    const list = (selectedRaces || []).filter((r) => {
      if (isObligatory(r)) return false;
      return categoryChoice === "UCI" ? isProSeries(r) : isContinental(r);
    });

    list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
    return list;
  }, [selectedRaces, categoryChoice]);

  const firstLineRaces = selectedForCategory.filter((_, index) => index % 2 === 0);
  const secondLineRaces = selectedForCategory.filter((_, index) => index % 2 !== 0);

  const removeRace = (raceId) => {
    setSelectedRaces((selectedRaces || []).filter((r) => getRaceId(r) !== raceId));
  };

  return (
    <main className="select-races">
      <div className="select-races-shadow-container">
        <div className="select-races-title">
          <h1>
            {t("selectRaces.title", {
              step: categoryChoice === "UCI" ? 1 : 2,
              totalSteps: 2,
            })}
          </h1>
          <div className="select-races-shadow-mask-right"></div>
          <div className="select-races-shadow-mask-bottom"></div>
        </div>
      </div>

      <div className="select-races-header">
        <div className="select-races-rules">
          <div>
            <p>
              {t("selectRaces.rules", {
                category: categoryChoice === "UCI" ? t("selectRaces.categories.uci") : t("selectRaces.categories.continental"),
              })}
            </p>
            {errorMessage ? <p className="select-races-error">{errorMessage}</p> : null}
          </div>
        </div>

        <div className="select-races-change-step">
          <button onClick={handlePrevStep}>{t("common.previous")}</button>
          <button onClick={handleNextStep} disabled={!canGoNext}>
            {t("common.next")}
          </button>
        </div>
      </div>

      <div className="select-races-pannel-view">
        <div className="select-races-choice">
          <div className="select-races-controls">
            <div className="select-races-sort-control">
              <label>{t("selectRaces.controls.defaultCalendar.label")}</label>
              <select value={calendarDefaultValue} onChange={handleDefaultCalendar}>
                <option value="aucun">{t("selectRaces.controls.defaultCalendar.none")}</option>
                <option value="Équilibré">{t("selectRaces.controls.defaultCalendar.balanced")}</option>
                <option value="CPA">{t("selectRaces.controls.defaultCalendar.cpa")}</option>
                <option value="Classique">{t("selectRaces.controls.defaultCalendar.classic")}</option>
              </select>
            </div>

            <div className="select-races-search-control">
              <label>{t("common.search")}</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("selectRaces.controls.searchPlaceholder")}
              />
            </div>

            <div className="select-races-sortby-control">
              <label>{t("common.sort")}</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">{t("selectRaces.sort.name")}</option>
                <option value="date">{t("selectRaces.sort.date")}</option>
                <option value="type">{t("selectRaces.sort.type")}</option>
                <option value="division">{t("selectRaces.sort.division")}</option>
              </select>
            </div>
          </div>

          <div className="select-races-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("selectRaces.table.race")}</th>
                  <th>{t("selectRaces.table.date")}</th>
                  <th>{t("selectRaces.table.category")}</th>
                  <th>{t("selectRaces.table.division")}</th>
                  <th>{t("selectRaces.table.type")}</th>
                  <th>{t("selectRaces.table.profile")}</th>
                  <th>{t("selectRaces.table.steps")}</th>
                </tr>
              </thead>

              <tbody>
                {viewRaces.map((race) => {
                  const id = getRaceId(race);
                  return (
                    <tr
                      key={id}
                      onClick={() => handleSelectRace(race)}
                      className={isRaceSelected(id) ? "select-races-table-selected-row" : ""}
                    >
                      <td>
                        <div className="select-races-table-name-container">
                          <img
                            className="select-races-table-flag-icon"
                            src={flagPath(race.country)}
                            alt={t("common.flagAlt", { country: race.country })}
                            width="20"
                            height="20"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.visibility = "hidden";
                            }}
                          />
                          <span>{race.name}</span>
                        </div>
                      </td>

                      <td>{race.date_start}</td>
                      <td>{race.category}</td>
                      <td>{normalizeDivisionLabel(race)}</td>
                      <td>{race.courseType}</td>
                      <td>
                        {icons[race.profil] ? (
                          <img src={icons[race.profil]} alt={race.profil} width="35" height="25" />
                        ) : (
                          race.profil
                        )}
                      </td>
                      <td>{race.step}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-races-choice-view">
          <p className="select-races-selected-number">
            {t("selectRaces.selectedCountLabel")} :
            <span
              className={
                selectedInCurrent === maxCurrent
                  ? "select-races-selected-races-green"
                  : "select-races-selected-races-red"
              }
            >
              {selectedInCurrent}
            </span>
            /{maxCurrent}
          </p>

          <div className="select-races-scroll-container">
            <div className="select-races-line-container">
              {firstLineRaces.map((r) => {
                const id = getRaceId(r);
                return (
                  <div className="select-races-selected-cell" key={id}>
                    <div className="select-races-selected-info">
                      <div className="select-races-selected-name-container">
                        <img
                          className="select-races-selected-flag-icon"
                          src={flagPath(r.country)}
                          alt={t("common.flagAlt", { country: r.country })}
                          width="20"
                          height="20"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                        <h4 title={r.name}>{r.name}</h4>
                      </div>
                      <p>{r.category}</p>
                      <p>{normalizeDivisionLabel(r)}</p>
                      <p>
                        {icons[r.profil] ? (
                          <img src={icons[r.profil]} alt={r.profil} width="35" height="25" />
                        ) : (
                          r.profil
                        )}
                      </p>

                      <button
                        className="select-races-remove-btn"
                        onClick={() => removeRace(id)}
                        type="button"
                      >
                        {t("common.remove")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="select-races-line-container">
              {secondLineRaces.map((r) => {
                const id = getRaceId(r);
                return (
                  <div className="select-races-selected-cell" key={id}>
                    <div className="select-races-selected-info">
                      <div className="select-races-selected-name-container">
                        <img
                          className="select-races-selected-flag-icon"
                          src={flagPath(r.country)}
                          alt={t("common.flagAlt", { country: r.country })}
                          width="20"
                          height="20"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                        <h4 title={r.name}>{r.name}</h4>
                      </div>
                      <p>{r.category}</p>
                      <p>{normalizeDivisionLabel(r)}</p>
                      <p>
                        {icons[r.profil] ? (
                          <img src={icons[r.profil]} alt={r.profil} width="35" height="25" />
                        ) : (
                          r.profil
                        )}
                      </p>

                      <button
                        className="select-races-remove-btn"
                        onClick={() => removeRace(id)}
                        type="button"
                      >
                        {t("common.remove")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {categoryChoice !== "UCI" && (
            <div className="select-races-global-counts">
              <p>
                {t("selectRaces.globalCounts.proSeries")} : <b>{selectedProCount}</b> / {MAX_PRO_SERIES}
              </p>
              <p>
                {t("selectRaces.globalCounts.continental")} : <b>{selectedContCount}</b> / {MAX_CONTINENTAL}
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default SelectRaces;
